import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Typography,
} from '@mui/material';

const FRAME_HANDLING_ORDER_NEW = 'order new';
const ACTION_PUT_BACK_TO_FLOOR = 'put_back_to_floor';
const ACTION_DISPOSE = 'dispose';
const ACTION_CANCEL_ORDER = 'cancel_order';

const ACTION_LABELS = {
    [ACTION_PUT_BACK_TO_FLOOR]: 'Put Back to Floor',
    [ACTION_DISPOSE]: 'Dispose',
    [ACTION_CANCEL_ORDER]: 'Cancel Order',
};

const normalizeOrderState = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'ordered') return 'ordered';
    if (normalized === 'not_ordered') return 'not_ordered';
    return 'ordered';
};

const buildInitialSelections = (previews = []) => {
    const next = {};

    previews.forEach((invoicePreview) => {
        const invoiceId = Number(invoicePreview?.invoiceId);
        (invoicePreview?.decisions || []).forEach((item) => {
            if (!item?.requiresDecision) {
                return;
            }

            const itemId = Number(item.invoiceItemId);
            if (!invoiceId || !itemId) {
                return;
            }

            const key = `${invoiceId}:${itemId}`;
            const orderState = normalizeOrderState(item.orderState);
            const allowedActions = Array.isArray(item.allowedActions) ? item.allowedActions : [];

            let action = item.defaultAction || allowedActions[0] || ACTION_DISPOSE;
            if (String(item.frameHandling || '').toLowerCase() === FRAME_HANDLING_ORDER_NEW && orderState === 'not_ordered') {
                action = ACTION_CANCEL_ORDER;
            }

            next[key] = {
                orderState,
                action,
            };
        });
    });

    return next;
};

export default function FrameDispositionDialog({
    open,
    previews = [],
    title = 'Frame Handling Required',
    confirmLabel = 'Continue',
    onClose,
    onConfirm,
    inProgress = false,
}) {
    const [selections, setSelections] = useState({});

    useEffect(() => {
        if (!open) {
            return;
        }
        setSelections(buildInitialSelections(previews));
    }, [open, previews]);

    const decisionItems = useMemo(() => {
        const items = [];
        previews.forEach((preview) => {
            const invoiceId = Number(preview?.invoiceId);
            const invoiceNumber = preview?.invoiceNumber || `Invoice #${invoiceId}`;

            (preview?.decisions || []).forEach((item) => {
                const itemId = Number(item.invoiceItemId);
                if (!itemId) {
                    return;
                }
                items.push({
                    invoiceId,
                    invoiceNumber,
                    ...item,
                });
            });
        });
        return items;
    }, [previews]);

    const requiredItems = decisionItems.filter((item) => item.requiresDecision);
    const infoItems = decisionItems.filter((item) => item.infoOnly);

    const handleOrderStateChange = (invoiceId, invoiceItemId, value) => {
        const key = `${invoiceId}:${invoiceItemId}`;
        setSelections((prev) => {
            const current = prev[key] || {};
            const orderState = normalizeOrderState(value);
            const nextAction = orderState === 'not_ordered' ? ACTION_CANCEL_ORDER : (current.action === ACTION_CANCEL_ORDER ? ACTION_PUT_BACK_TO_FLOOR : (current.action || ACTION_PUT_BACK_TO_FLOOR));
            return {
                ...prev,
                [key]: {
                    ...current,
                    orderState,
                    action: nextAction,
                }
            };
        });
    };

    const handleActionChange = (invoiceId, invoiceItemId, value) => {
        const key = `${invoiceId}:${invoiceItemId}`;
        setSelections((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                action: value,
            }
        }));
    };

    const canSubmit = useMemo(() => {
        return requiredItems.every((item) => {
            const key = `${item.invoiceId}:${item.invoiceItemId}`;
            const selected = selections[key];
            return Boolean(selected?.action);
        });
    }, [requiredItems, selections]);

    const handleConfirm = () => {
        const decisionsByInvoice = {};

        requiredItems.forEach((item) => {
            const key = `${item.invoiceId}:${item.invoiceItemId}`;
            const selected = selections[key];
            if (!selected?.action) {
                return;
            }

            if (!decisionsByInvoice[item.invoiceId]) {
                decisionsByInvoice[item.invoiceId] = [];
            }

            decisionsByInvoice[item.invoiceId].push({
                invoiceItemId: item.invoiceItemId,
                action: selected.action,
            });
        });

        onConfirm(decisionsByInvoice);
    };

    return (
        <Dialog open={open} onClose={inProgress ? undefined : onClose} maxWidth='md' fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {requiredItems.length === 0 && infoItems.length === 0 && (
                    <Typography>No frame disposition decisions are required.</Typography>
                )}

                <Stack spacing={2}>
                    {requiredItems.map((item) => {
                        const key = `${item.invoiceId}:${item.invoiceItemId}`;
                        const selected = selections[key] || {};
                        const frameHandling = String(item.frameHandling || '').toLowerCase();
                        const effectiveOrderState = frameHandling === FRAME_HANDLING_ORDER_NEW
                            ? normalizeOrderState(selected.orderState || item.orderState)
                            : null;

                        let actionOptions = Array.isArray(item.allowedActions) ? item.allowedActions : [];
                        if (frameHandling === FRAME_HANDLING_ORDER_NEW) {
                            actionOptions = effectiveOrderState === 'not_ordered'
                                ? [ACTION_CANCEL_ORDER]
                                : [ACTION_PUT_BACK_TO_FLOOR, ACTION_DISPOSE];
                        }

                        return (
                            <Box key={key} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant='subtitle2'>{item.invoiceNumber}</Typography>
                                <Typography variant='body2' sx={{ mb: 1 }}>
                                    {item.description || 'Frame item'} x {Number(item.quantity || 1)} ({item.frameHandling || 'frame'})
                                </Typography>

                                {frameHandling === FRAME_HANDLING_ORDER_NEW && (
                                    <FormControl sx={{ mb: 1 }}>
                                        <Typography variant='body2' sx={{ mb: 0.5 }}>Has this frame already been ordered?</Typography>
                                        <RadioGroup
                                            row
                                            value={effectiveOrderState || 'ordered'}
                                            onChange={(event) => handleOrderStateChange(item.invoiceId, item.invoiceItemId, event.target.value)}
                                        >
                                            <FormControlLabel value='ordered' control={<Radio size='small' />} label='Yes' />
                                            <FormControlLabel value='not_ordered' control={<Radio size='small' />} label='No' />
                                        </RadioGroup>
                                    </FormControl>
                                )}

                                <FormControl fullWidth size='small'>
                                    <Typography variant='body2' sx={{ mb: 0.5 }}>Disposition</Typography>
                                    <Select
                                        value={selected.action || ''}
                                        onChange={(event) => handleActionChange(item.invoiceId, item.invoiceItemId, event.target.value)}
                                        disabled={frameHandling === FRAME_HANDLING_ORDER_NEW && effectiveOrderState === 'not_ordered'}
                                    >
                                        {actionOptions.map((action) => (
                                            <MenuItem key={action} value={action}>{ACTION_LABELS[action] || action}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        );
                    })}

                    {infoItems.length > 0 && (
                        <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>Information Only</Typography>
                            {infoItems.map((item) => (
                                <Typography key={`${item.invoiceId}:${item.invoiceItemId}`} variant='body2'>
                                    {item.invoiceNumber}: {item.description || 'Frame item'} is patient-owned and will not affect inventory.
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={inProgress}>Cancel</Button>
                <Button variant='contained' onClick={handleConfirm} disabled={!canSubmit || inProgress}>{confirmLabel}</Button>
            </DialogActions>
        </Dialog>
    );
}
